import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { FC, useState } from "react"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { FileExtension } from "@nova/constants"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"

const MAX_MISSION_FILE_BYTES = 100 * 1024 * 1024 // 100mb

export type MissionFileUploadData = {
    name: string
    notes: string | null
    expiresAt: string | null
    file: File
}

const ALLOWED_FILE_TYPES = [
    FileExtension.pdf,
    FileExtension.kml,
    FileExtension.png,
    FileExtension.jpg,
    FileExtension.jpeg,
]

export const UploadMissionFileModal: FC<{
    confirm: (args: MissionFileUploadData) => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
    isPerformingAsyncCancelCallback: boolean
}> = ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => {
    const [name, setName] = useState("")
    const [expiresAt, setExpiresAt] = useState("")
    const [notes, setNotes] = useState("")
    const [file, setFile] = useState<File | null>(null)
    const [fileSizeError, setFileSizeError] = useState(false)

    const canUpload = !!file && !isPerformingAsyncConfirmCallback

    const handleNewFile = async (files: File[]) => {
        const selected = files[0]
        if (selected.size > MAX_MISSION_FILE_BYTES) {
            setFileSizeError(true)
            return false
        }
        setFileSizeError(false)
        setFile(selected)
        if (!name) {
            setName(selected.name)
        }
    }

    const handleSubmit = () => {
        if (!file) return
        confirm({
            name: name || file.name,
            notes: notes || null,
            expiresAt: expiresAt || null,
            file,
        })
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-[480px] w-full"
            title="Upload File"
            closeModal={cancel}
            bottomBar={
                <div className="flex justify-end gap-2 w-full">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={cancel}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                    <ButtonPrimary
                        title="Upload File"
                        onClick={handleSubmit}
                        disabled={!canUpload}
                        isLoading={isPerformingAsyncConfirmCallback}
                    />
                </div>
            }
        >
            <div className="flex flex-col gap-4 pt-2">
                <InputOutlined
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                />

                <div className="w-1/2">
                    <InputOutlined
                        type="date"
                        label="Date Expires"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.currentTarget.value)}
                    />
                </div>

                <TextAreaOutlined
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                    rows={5}
                />

                <div className="flex flex-col gap-2">
                    <UploadDropBox
                        allowedFileTypes={ALLOWED_FILE_TYPES}
                        onNewItems={handleNewFile}
                        isProcessing={isPerformingAsyncConfirmCallback}
                        title={file ? file.name : "Upload a File"}
                        supportedMimeTypes={[]}
                        width="100%"
                    />

                    {fileSizeError && (
                        <p className="text-red-400 text-sm">
                            File too large. The max allowed size is{" "}
                            {MAX_MISSION_FILE_BYTES / 1024 / 1024} MB.
                        </p>
                    )}
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}
